import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { visibleProposedTimes } from "./visibleProposedTimes";
import { normalizeRange, fmtMin12 } from "./proposalRange";
import { blockStyle, statusKeyFor } from "../constants/locationTints";

// ── Weekly time grid (Network + Clinic "Calendar" views) ────────────────────
//
// Renders negotiation state: confirmed appointments as solid blocks,
// in-flight proposals as lighter striped RANGE blocks, on a 07:00–19:00 grid
// with 15-minute rows. Interaction contract (2026-06 pivot — a DELIBERATE
// relaxation of the earlier read-only pins):
//   • empty-cell clicks = two-click range creation (proposeMode — internal
//     portal only)
//   • click inside a proposal block = pick a 15-min slot to confirm
//     (confirmMode — clinic portal only)
//   • everything else stays inert; empty cells carry ZERO availability
//     styling
// This component still performs NO network calls of any kind — clicks are
// input; the page owns every submit/confirm pipeline.

const ROW_H = 20; // px per 15-min row → 48 rows = 960px lane height
const DAY_START_MIN = 7 * 60; // 07:00
const DAY_END_MIN = 19 * 60; // 19:00
const ROWS = (DAY_END_MIN - DAY_START_MIN) / 15; // 48

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const fmt12 = (hms) => {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};

const fmtHour = (h) => `${((h + 11) % 12) + 1} ${h >= 12 ? "PM" : "AM"}`;

// row index ↔ time (rows ARE the 15-minute grid — snap is inherent).
// Emits HH:MM to match the form's SLOT_OPTIONS values (backend
// _normalize_time accepts both HH:MM and HH:MM:SS).
export const rowToHMS = (row) => {
  const min = DAY_START_MIN + row * 15;
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
};
export const rowToMin = (row) => DAY_START_MIN + row * 15;

// THE single week-start helper (2026-06: Sunday-start weeks, per Yesenia's
// team — paper-schedule familiarity). Both portals import this; no local
// week math may exist anywhere else (statically pinned).
export function startOfWeek(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - x.getDay()); // 0=Sun..6=Sat → back to Sunday
  x.setHours(0, 0, 0, 0);
  return x;
}

// Two-click geometry, pure + exported for tests:
//   no anchor              → this cell becomes the anchor
//   same day, at-or-after  → COMPLETE: end = clicked row + 1 (i.e. +15 min,
//                            so anchor-click + same-cell-click = one slot)
//   different day / earlier→ re-anchor to the clicked cell
export function nextProposeClick(anchor, cell) {
  if (!anchor) return { anchor: cell, range: null };
  if (cell.iso === anchor.iso && cell.row >= anchor.row) {
    return {
      anchor: null,
      range: { iso: anchor.iso, startRow: anchor.row, endRow: cell.row + 1 },
    };
  }
  return { anchor: cell, range: null };
}

// Visual style per state. Confirmed = the is_confirmed time of a locked_in
// request (solid). Everything else = in-flight (lighter + striped).
const STRIPES = {
  backgroundImage:
    "repeating-linear-gradient(45deg, rgba(255,255,255,0.55) 0 4px, transparent 4px 8px)",
};
const blockClasses = (request, time) => {
  if (request.status === "direct_booking") {
    // Direct bookings (no scheduling request) — solid indigo, no existing
    // status on either portal uses this hue.
    return { cls: "bg-indigo-500 border-indigo-600 text-white", striped: false };
  }
  if (request.status === "locked_in" && time.is_confirmed) {
    return { cls: "bg-emerald-500 border-emerald-600 text-white", striped: false };
  }
  if (request.status === "suggested_alternative") {
    return { cls: "bg-amber-100 border-amber-400 text-amber-900", striped: true };
  }
  if (request.status === "cancelled") {
    return { cls: "bg-slate-100 border-slate-300 text-slate-500", striped: true };
  }
  return { cls: "bg-blue-100 border-blue-300 text-blue-900", striped: true }; // pending
};

// Overlap handling: greedy lane assignment inside each overlapping cluster;
// blocks in a cluster share the lane width side-by-side, never hidden.
function layoutDay(items) {
  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin
  );
  const clusters = [];
  let cur = null;
  for (const it of sorted) {
    if (!cur || it.startMin >= cur.maxEnd) {
      cur = { items: [], maxEnd: it.endMin };
      clusters.push(cur);
    }
    cur.items.push(it);
    cur.maxEnd = Math.max(cur.maxEnd, it.endMin);
  }
  const placed = [];
  for (const c of clusters) {
    const laneEnds = [];
    const mine = [];
    for (const it of c.items) {
      let lane = laneEnds.findIndex((e) => e <= it.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = it.endMin;
      mine.push({ ...it, lane });
    }
    mine.forEach((p) => placed.push({ ...p, lanes: laneEnds.length }));
  }
  return placed;
}

export const NetworkCalendarGrid = ({
  weekStart,
  requests,
  onBlockClick,
  // Generalization for the clinic portal (2026-06): clinics see only their
  // own location, so the "· L100" suffix is noise there. Default true keeps
  // the network page pixel-equivalent without prop changes.
  showLocationCode = true,
  // Range pivot (2026-06):
  proposeMode = false, // internal: empty-cell two-click range creation
  onRangeComplete, // ({ date, start_time, end_time })
  confirmMode = false, // clinic: in-block slot pick
  onSlotPick, // ({ request, time, date, slot_start })
  // Spotlight (clinic): emphasize one request, dim the rest. View-state only.
  spotlightRequestId = null,
  // Per-location color coding (2026-08, multi-location clinic logins ONLY —
  // default null keeps every other consumer pixel-identical). Hue renders as
  // a left stripe + short text label; existing state classes are untouched
  // and spotlight emphasis/dimming + suppression take precedence (opacity
  // and ring apply OVER the stripe; suppressed times never render at all).
  locationColors = null,
  locationLabels = null,
  // B.1/B.2 (2026-06, multi-location CLINIC calendar only — default false
  // keeps the internal Network page and single-location clinics
  // byte-identical): the whole block takes the LOCATION's tint and prints
  // the location's name, and status is carried by SHAPE only (check icon,
  // Direct chip, dashed border) instead of the status hue classes above.
  locationIdentity = false,
  // A.1 (2026-09 booking mode): in browse mode a spotlight dims everything
  // that is not the chosen request. Booking mode answers that question
  // structurally — it doesn't render the irrelevant — so passing an array
  // here restricts dimming to EXACTLY those request ids (the pending work
  // re-revealed by "Show other pending requests"). `null` keeps the
  // original dim-everything-else behaviour for every other consumer.
  dimmedRequestIds = null,
  // Propose session drafts (2026-06 — network only, default off): completed
  // but not-yet-submitted range windows, rendered as dashed draft blocks.
  // View-state only; the page owns the array.
  draftRanges = [],
  // Reserved WestStar slots (2026-02 Phase 2, default off): standing
  // placeholders rendered as slate-dashed + striped blocks. Booked slots
  // are NOT rendered here (the CRMAppointment/direct booking already
  // renders as a Direct chip). Blocked = strike + reduced opacity.
  // Past = grayed inert (both modes). reservedMode governs interaction:
  //   "none"     → view-only (external consumers passing no prop stay
  //                byte-identical — pinned by canary tests)
  //   "clinic"   → click OPEN=block, BLOCKED=unblock (page handles confirm)
  //   "internal" → click OPEN=claim (page opens claim dialog); adds a
  //                hover "Claim →" affordance
  reservedSlots = [],
  reservedMode = "none",
  onReservedClick,
  // Paint mode (2026-02 phase 2, default off): the clinic "WestStar
  // Reserve" toggle. When active, an empty-cell click creates a pending
  // add; a click on an existing OPEN/BLOCKED placeholder creates a
  // pending remove; a click on a pending marker undoes it. The page
  // owns the pending state — the grid is a pure input surface.
  //
  // Booked slots stay inert in paint mode (real appointments). Sunday
  // and past cells stay inert. Paint mode and proposeMode never both
  // run — the internal (proposeMode) and clinic (paintMode) surfaces
  // are separate pages with disjoint consumer state.
  paintMode = false,
  paintedAdds = [],     // [{ date: "YYYY-MM-DD", slot_time: "HH:MM:SS" }]
  paintedRemoves = [],  // same shape (each MUST match an existing reserved-slot identity)
  onPaintCell,          // ({ iso, slot_time, existingState })  page-owned handler
}) => {
  const [anchor, setAnchor] = useState(null); // { iso, row }
  const [hoverCell, setHoverCell] = useState(null); // { iso, row }
  const [hoverSlot, setHoverSlot] = useState(null); // { key, idx }

  // Escape cancels an in-progress range anchor
  useEffect(() => {
    if (!proposeMode || !anchor) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAnchor(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [proposeMode, anchor]);

  const handleCellClick = (iso, row) => {
    const { anchor: nextAnchor, range } = nextProposeClick(anchor, { iso, row });
    setAnchor(nextAnchor);
    if (range && onRangeComplete) {
      onRangeComplete({
        date: range.iso,
        start_time: rowToHMS(range.startRow),
        end_time: rowToHMS(range.endRow),
      });
    }
  };

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const x = new Date(weekStart);
        x.setDate(x.getDate() + i);
        return x;
      }),
    [weekStart]
  );

  // "Now" as (todayISO, HH:MM:SS) computed ONCE per render — used only to
  // decide if a reserved-slot placeholder should render as past/inert.
  // The slot's own `date` + `slot_time` values are compared lexically as
  // strings; no `new Date()` is applied to the slot's fields (pinned).
  const nowRef = useMemo(() => {
    const n = new Date();
    const iso = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    const t = `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}:${String(n.getSeconds()).padStart(2, "0")}`;
    return { iso, t };
  }, [weekStart]); // recompute on week change

  const isReservedPast = (r) => {
    if (!r?.date || !r?.slot_time) return false;
    if (r.date < nowRef.iso) return true;
    if (r.date > nowRef.iso) return false;
    // same day — compare wall-clock times as strings (HH:MM:SS lexical order
    // is chronological).
    const st = r.slot_time.length === 5 ? `${r.slot_time}:00` : r.slot_time;
    return st <= nowRef.t;
  };

  // Paint-mode helpers — cell-level "is past?" and "is Sunday?" checks
  // that operate on ISO date + HH:MM:SS strings only (never construct a
  // Date object from a slot_time).
  const isPastCell = (iso, hms) => {
    if (iso < nowRef.iso) return true;
    if (iso > nowRef.iso) return false;
    return hms <= nowRef.t;
  };
  const isPastDay = (iso) => iso < nowRef.iso;
  const isSundayISO = (iso) => {
    // Compute weekday from the ISO date parts (local noon anchor to
    // sidestep DST — this is a pure DATE, not a datetime).
    const [y, mo, d] = iso.split("-").map((s) => parseInt(s, 10));
    const dt = new Date(y, mo - 1, d, 12, 0, 0);
    return dt.getDay() === 0;
  };

  // Group reserved slots by date, filter out ones outside the visible week
  // and BOOKED (backend guarantees claimed slots show up as a
  // CRMAppointment/direct booking — no double render).
  const reservedByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      map[`${y}-${m}-${dd}`] = [];
    });
    (reservedSlots || []).forEach((r) => {
      if (r.state === "booked") return; // rendered elsewhere
      if (map[r.date] === undefined) return;
      map[r.date].push(r);
    });
    return map;
  }, [reservedSlots, days]);

  // Same payload the request cards use — only the shared suppression helper
  // decides which times are visible; only the shared range helper measures.
  const blocksByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => {
      map[toISODate(d)] = [];
    });
    requests.forEach((r) => {
      visibleProposedTimes(r).forEach((p) => {
        if (map[p.date] === undefined) return;
        const { startMin, endMin } = normalizeRange(p);
        if (endMin <= DAY_START_MIN || startMin >= DAY_END_MIN) return;
        map[p.date].push({ request: r, time: p, startMin, endMin });
      });
    });
    Object.keys(map).forEach((k) => {
      map[k] = layoutDay(map[k]);
    });
    return map;
  }, [requests, days]);

  const todayISO = toISODate(new Date());

  // A block accepts an in-block slot click only when the clinic could
  // actually confirm it (mirrors the server's own rejections).
  const confirmable = (b) =>
    confirmMode &&
    !b.time.is_confirmed &&
    b.time.source === "weststar" &&
    b.request.status !== "locked_in" &&
    b.request.status !== "cancelled";

  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden"
      data-testid="network-calendar-grid"
    >
      {proposeMode && anchor && (
        <div
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border-b border-blue-200 text-xs text-blue-800"
          data-testid="propose-hint-chip"
        >
          <span className="font-semibold">
            Range start: {fmtMin12(rowToMin(anchor.row))} on {anchor.iso}
          </span>
          — now click the end time (Esc to cancel)
        </div>
      )}
      <div className="overflow-auto max-h-[560px]">
        <div
          className="grid min-w-[840px]"
          style={{ gridTemplateColumns: "56px repeat(7, minmax(0, 1fr))" }}
        >
          {/* Sticky header row */}
          <div className="sticky top-0 left-0 z-30 bg-white border-b border-r border-slate-200" />
          {days.map((d) => {
            const iso = toISODate(d);
            const isToday = iso === todayISO;
            return (
              <div
                key={iso}
                data-testid={`cal-day-header-${iso}`}
                className={`sticky top-0 z-20 px-2 py-1.5 border-b border-r last:border-r-0 border-slate-200 text-center ${
                  isToday ? "bg-blue-50" : "bg-white"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div
                  className={`text-sm font-bold leading-tight ${
                    isToday ? "text-blue-700" : "text-slate-900"
                  }`}
                >
                  {d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                </div>
              </div>
            );
          })}

          {/* Sticky time column — labels at hour marks, quarter rows unlabeled */}
          <div
            className="sticky left-0 z-10 bg-white border-r border-slate-200"
            data-testid="cal-time-column"
            style={{ height: ROWS * ROW_H }}
          >
            {Array.from({ length: ROWS }, (_, i) => {
              const min = DAY_START_MIN + i * 15;
              const isHour = min % 60 === 0;
              return (
                <div
                  key={min}
                  className={`text-right pr-1.5 border-t ${
                    isHour ? "border-slate-200" : "border-slate-100/60"
                  }`}
                  style={{ height: ROW_H }}
                >
                  {isHour && (
                    <span className="text-[9px] text-slate-500 font-medium leading-none relative -top-0.5">
                      {fmtHour(min / 60)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Day lanes — plain grid background, absolutely-positioned blocks */}
          {days.map((d) => {
            const iso = toISODate(d);
            const blocks = blocksByDay[iso] || [];
            const previewActive =
              proposeMode && anchor && anchor.iso === iso && hoverCell?.iso === iso;
            const previewStart = previewActive
              ? Math.min(anchor.row, hoverCell.row)
              : null;
            const previewEnd = previewActive
              ? Math.max(anchor.row, hoverCell.row) + 1
              : null;
            return (
              <div
                key={iso}
                data-testid={`cal-day-lane-${iso}`}
                className="relative border-r last:border-r-0 border-slate-100"
                style={{ height: ROWS * ROW_H }}
                onMouseLeave={() => setHoverCell(null)}
              >
                {/* hour lines emphasized, quarter-hour lines subtle; the
                    lines themselves carry no handlers */}
                {Array.from({ length: ROWS }, (_, i) => (
                  <div
                    key={i}
                    className={`absolute inset-x-0 border-t pointer-events-none ${
                      i % 4 === 0 ? "border-slate-200" : "border-slate-100/60"
                    }`}
                    style={{ top: i * ROW_H }}
                  />
                ))}

                {/* proposeMode click layer — transparent 15-min cells. NO
                    availability styling; the only visuals are the anchor
                    marker and the anchor→hover range preview. */}
                {proposeMode &&
                  Array.from({ length: ROWS }, (_, i) => (
                    <div
                      key={`cell-${i}`}
                      data-testid={`propose-cell-${iso}-${i}`}
                      onClick={() => handleCellClick(iso, i)}
                      onMouseEnter={() => setHoverCell({ iso, row: i })}
                      className="absolute inset-x-0 cursor-pointer"
                      style={{ top: i * ROW_H, height: ROW_H }}
                    />
                  ))}
                {previewActive && (
                  <div
                    data-testid={`propose-preview-${iso}`}
                    className="absolute inset-x-0.5 rounded bg-blue-200/50 border border-blue-400 border-dashed pointer-events-none z-[5]"
                    style={{
                      top: previewStart * ROW_H,
                      height: (previewEnd - previewStart) * ROW_H,
                    }}
                  />
                )}
                {proposeMode && anchor && anchor.iso === iso && (
                  <div
                    data-testid="propose-anchor-marker"
                    className="absolute inset-x-0.5 rounded bg-blue-500/80 text-white text-[9px] font-bold px-1 leading-[18px] z-[6] flex items-center justify-between"
                    style={{ top: anchor.row * ROW_H, height: ROW_H }}
                  >
                    <span>{fmtMin12(rowToMin(anchor.row))}</span>
                    <button
                      type="button"
                      data-testid="propose-anchor-cancel"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchor(null);
                      }}
                      className="px-1 hover:text-blue-100"
                      title="Cancel range"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Draft blocks — dashed styling distinguishes not-yet-
                    submitted session windows from real proposal blocks. */}
                {draftRanges.map((r, i) => {
                  if (r.date !== iso) return null;
                  const { startMin, endMin } = normalizeRange(r);
                  if (endMin <= DAY_START_MIN || startMin >= DAY_END_MIN) return null;
                  return (
                    <div
                      key={`draft-${i}`}
                      data-testid={`draft-block-${iso}-${i}`}
                      className="absolute inset-x-0.5 rounded border-2 border-dashed border-blue-500 bg-blue-100/70 text-blue-900 pointer-events-none z-[4] overflow-hidden"
                      style={{
                        top: ((Math.max(startMin, DAY_START_MIN) - DAY_START_MIN) / 15) * ROW_H,
                        height:
                          ((Math.min(endMin, DAY_END_MIN) - Math.max(startMin, DAY_START_MIN)) / 15) *
                          ROW_H,
                      }}
                    >
                      <span className="block px-1 text-[9px] font-bold leading-[1.15] truncate">
                        {fmtMin12(startMin)} – {fmtMin12(endMin)} · draft
                      </span>
                    </div>
                  );
                })}

                {/* Reserved WestStar slots — slate-dashed + striped
                    standing placeholders. Booked slots are NOT rendered
                    here (a Direct chip already covers them). Blocked =
                    strike + reduced opacity. Past = grayed inert on both
                    sides. Interaction gated on reservedMode. */}
                {(reservedByDay[iso] || []).map((r) => {
                  const st = r.slot_time.length === 5 ? `${r.slot_time}:00` : r.slot_time;
                  const [hh, mm] = st.split(":");
                  const startMin = parseInt(hh, 10) * 60 + parseInt(mm, 10);
                  if (startMin < DAY_START_MIN || startMin >= DAY_END_MIN) return null;
                  const endMin = Math.min(startMin + 15, DAY_END_MIN);
                  const top = ((startMin - DAY_START_MIN) / 15) * ROW_H;
                  const height = ((endMin - startMin) / 15) * ROW_H - 1;
                  const past = isReservedPast(r);
                  const isOpen = r.state === "open";
                  const isBlocked = r.state === "blocked";
                  const isBooked = r.state === "booked";
                  // Paint-mode intercept: identity is (iso, slot_time).
                  const pendingRemove = paintMode && paintedRemoves.some(
                    (p) => p.date === iso && (p.slot_time.length === 5 ? `${p.slot_time}:00` : p.slot_time) === st
                  );
                  const paintClickable =
                    paintMode && !past && !isBooked;  // pending-remove or plain click on open/blocked
                  const normalClickable =
                    !paintMode && !past &&
                    ((reservedMode === "clinic" && (isOpen || isBlocked)) ||
                      (reservedMode === "internal" && isOpen));
                  const clickable = paintClickable || normalClickable;
                  // Time-only labels (slot_label removed in the redesign).
                  const compactTime = fmt12(r.slot_time).replace(/\s/g, "").replace("AM", "a").replace("PM", "p");
                  const label = `WS ${compactTime}`;
                  const fullLabel = `WS · ${fmt12(r.slot_time)}`;
                  const bgClass = past
                    ? "bg-slate-100/70 text-slate-400 border-slate-300"
                    : pendingRemove
                    ? "bg-rose-50 text-rose-700 border-rose-400"
                    : isBlocked
                    ? "bg-slate-100 text-slate-500 border-slate-400"
                    : "bg-slate-50 text-slate-700 border-slate-400";
                  const opacity = (isBlocked && !past && !pendingRemove) ? "opacity-70" : "";
                  const cursor = clickable ? "cursor-pointer" : "cursor-default";
                  const testid = `reserved-slot-${iso}-${st}`;
                  const handleClick = paintMode
                    ? () => onPaintCell && onPaintCell({ iso, slot_time: st, existingState: r.state })
                    : (normalClickable && onReservedClick ? () => onReservedClick(r) : undefined);
                  return (
                    <div
                      key={`res-${iso}-${st}`}
                      data-testid={testid}
                      data-state={past ? "expired" : (pendingRemove ? "pending-remove" : r.state)}
                      role={clickable ? "button" : "img"}
                      tabIndex={clickable ? 0 : -1}
                      title={
                        past
                          ? `${fullLabel} · past`
                          : pendingRemove
                          ? `${fullLabel} · pending remove — click to undo`
                          : paintMode
                          ? `${fullLabel} · click to remove`
                          : isBlocked
                          ? `${fullLabel} · blocked${r.blocked_by ? ` by ${r.blocked_by}` : ""}${reservedMode === "clinic" ? " · click to unblock" : ""}`
                          : reservedMode === "internal"
                          ? `${fullLabel} · open · click to claim`
                          : reservedMode === "clinic"
                          ? `${fullLabel} · open · click to block`
                          : `${fullLabel} · open`
                      }
                      onClick={handleClick}
                      className={`absolute inset-x-0.5 rounded border-2 border-dashed overflow-hidden group flex items-center ${bgClass} ${opacity} ${cursor} ${
                        !past && !isBlocked && !paintMode && reservedMode === "internal"
                          ? "hover:bg-blue-50 hover:border-blue-500 hover:text-blue-900 active:bg-blue-100"
                          : !past && !paintMode && reservedMode === "clinic"
                          ? "hover:border-slate-600"
                          : ""
                      }`}
                      style={{
                        top,
                        height,
                        backgroundImage:
                          "repeating-linear-gradient(45deg, rgba(148,163,184,0.18) 0 4px, transparent 4px 8px)",
                        zIndex: 3,
                      }}
                    >
                      <span
                        className={`block w-full px-1 text-[9px] font-semibold leading-none truncate relative z-[2] ${
                          isBlocked && !past && !pendingRemove ? "line-through" : ""
                        } ${pendingRemove ? "line-through" : ""} ${!past && !isBlocked && !paintMode && reservedMode === "internal" ? "group-hover:hidden" : ""}`}
                      >
                        {pendingRemove ? `${label} · remove` : label}
                      </span>
                      {!past && !isBlocked && !paintMode && reservedMode === "internal" && (
                        <span
                          data-testid={`reserved-claim-hint-${iso}-${st}`}
                          className="hidden group-hover:block w-full px-1 text-[9px] font-bold text-center bg-blue-600 text-white leading-none py-[3px] relative z-[3]"
                        >
                          Claim →
                        </span>
                      )}
                      {isBlocked && !past && !pendingRemove && (
                        <span
                          data-testid={`reserved-blocked-strike-${iso}-${st}`}
                          className="absolute inset-0 pointer-events-none flex items-center justify-center"
                        >
                          <span className="w-full h-[2px] bg-slate-500/60 rotate-[-6deg]" />
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Paint-mode: pending-add markers (blue dashed, distinct
                    from committed slate placeholders). Rendered from the
                    paintedAdds page-owned array. Click to undo. */}
                {paintMode && paintedAdds.filter((p) => p.date === iso).map((p) => {
                  const st = p.slot_time.length === 5 ? `${p.slot_time}:00` : p.slot_time;
                  const [hh, mm] = st.split(":");
                  const startMin = parseInt(hh, 10) * 60 + parseInt(mm, 10);
                  if (startMin < DAY_START_MIN || startMin >= DAY_END_MIN) return null;
                  const top = ((startMin - DAY_START_MIN) / 15) * ROW_H;
                  const height = ROW_H - 1;
                  const compactTime = fmt12(st).replace(/\s/g, "").replace("AM", "a").replace("PM", "p");
                  return (
                    <div
                      key={`pending-add-${iso}-${st}`}
                      data-testid={`reserved-pending-add-${iso}-${st}`}
                      data-state="pending-add"
                      role="button"
                      tabIndex={0}
                      title={`WS · ${fmt12(st)} · pending add — click to undo`}
                      onClick={() => onPaintCell && onPaintCell({ iso, slot_time: st, existingState: "pending-add" })}
                      className="absolute inset-x-0.5 rounded border-2 border-dashed border-blue-500 bg-blue-50 text-blue-800 overflow-hidden cursor-pointer flex items-center hover:bg-blue-100"
                      style={{ top, height, zIndex: 4 }}
                    >
                      <span className="block w-full px-1 text-[9px] font-bold leading-none truncate">
                        + WS {compactTime}
                      </span>
                    </div>
                  );
                })}

                {/* Paint-mode click layer over empty 15-min cells. Cells
                    that already host a reserved slot (committed or
                    pending) are covered by those blocks (higher z-index)
                    and swallow the click. Sunday cells + past cells are
                    left as regular grid — no paint layer over them. */}
                {paintMode && !isPastDay(iso) && !isSundayISO(iso) &&
                  Array.from({ length: ROWS }, (_, i) => {
                    const minAbs = DAY_START_MIN + i * 15;
                    const st = `${String(Math.floor(minAbs / 60)).padStart(2, "0")}:${String(minAbs % 60).padStart(2, "0")}:00`;
                    // Skip if a committed reserved slot or a pending-add
                    // already occupies this cell (their divs render on
                    // top with z-index 3/4 anyway — this is a defensive
                    // no-op that also keeps hit-testing predictable).
                    if ((reservedByDay[iso] || []).some((r) =>
                      (r.slot_time.length === 5 ? `${r.slot_time}:00` : r.slot_time) === st
                    )) return null;
                    if (paintedAdds.some((p) => p.date === iso &&
                      (p.slot_time.length === 5 ? `${p.slot_time}:00` : p.slot_time) === st
                    )) return null;
                    if (isPastCell(iso, st)) return null;
                    return (
                      <div
                        key={`paint-cell-${iso}-${i}`}
                        data-testid={`paint-cell-${iso}-${i}`}
                        onClick={() => onPaintCell && onPaintCell({ iso, slot_time: st, existingState: null })}
                        className="absolute inset-x-0 cursor-pointer hover:bg-blue-100/40"
                        style={{ top: i * ROW_H, height: ROW_H, zIndex: 2 }}
                      />
                    );
                  })}

                {blocks.map((b) => {
                  const top =
                    ((Math.max(b.startMin, DAY_START_MIN) - DAY_START_MIN) / 15) * ROW_H;
                  const height = Math.max(
                    ((Math.min(b.endMin, DAY_END_MIN) -
                      Math.max(b.startMin, DAY_START_MIN)) /
                      15) *
                      ROW_H -
                      1,
                    ROW_H - 2
                  );
                  const widthPct = 100 / b.lanes;
                  const statusKey = statusKeyFor(b.request, b.time);
                  const { cls: stateCls, striped: stateStriped } = blockClasses(b.request, b.time);
                  // B.1/B.2: in identity mode the hue belongs to the
                  // LOCATION, so the status hue classes are replaced by a
                  // neutral shell + the tint style below, and the 45°
                  // stripes (a status signal) are dropped.
                  const cls = locationIdentity ? "border-transparent" : stateCls;
                  const striped = locationIdentity ? false : stateStriped;
                  const patient =
                    b.request.patient_name || `Patient #${b.request.patient_id}`;
                  const locSuffix =
                    showLocationCode && b.request.location_code
                      ? ` · ${b.request.location_code}`
                      : "";
                  // Location tint (multi-location clinics): stripe + label.
                  const locEntry = locationColors?.[b.request.location_code] || null;
                  const locLabel = locEntry
                    ? `${locationLabels?.[b.request.location_code] || b.request.location_code}${locEntry.suffix || ""}`
                    : null;
                  // B.3: the name is PRINTED when the block is tall enough
                  // for a second line; otherwise the tooltip carries it —
                  // a 15-minute block never hides which clinic it belongs
                  // to, it just says it on hover.
                  const printLocLine = locationIdentity && !!locLabel && height >= ROW_H * 2;
                  const key = `${b.request.id}-${b.time.id}`;
                  const canPick = confirmable(b);
                  const slotCount = Math.round((b.endMin - b.startMin) / 15);
                  const hovIdx = hoverSlot?.key === key ? hoverSlot.idx : null;
                  // Spotlight: emphasized vs dimmed-to-background
                  const spot =
                    spotlightRequestId == null
                      ? ""
                      : b.request.id === spotlightRequestId
                        ? " ring-2 ring-blue-500 ring-offset-1 z-10 brightness-105"
                        : dimmedRequestIds
                          ? (dimmedRequestIds.includes(b.request.id)
                              ? " opacity-30 pointer-events-none"
                              : "")
                          : " opacity-30 pointer-events-none";
                  return (
                    <button
                      type="button"
                      key={key}
                      data-testid={`cal-block-${b.request.id}-${b.time.id}`}
                      data-loc-display={
                        locationIdentity ? (printLocLine ? "printed" : "tooltip") : undefined
                      }
                      data-status-shape={locationIdentity ? statusKey : undefined}
                      onClick={(e) => {
                        if (canPick && onSlotPick) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const idx = Math.min(
                            Math.max(Math.floor((e.clientY - rect.top) / ROW_H), 0),
                            slotCount - 1
                          );
                          onSlotPick({
                            request: b.request,
                            time: b.time,
                            date: b.time.date,
                            slot_start: rowToHMS(
                              (b.startMin - DAY_START_MIN) / 15 + idx
                            ),
                          });
                        } else if (onBlockClick) {
                          onBlockClick(b.request.id);
                        }
                      }}
                      onMouseMove={
                        canPick
                          ? (e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const idx = Math.min(
                                Math.max(
                                  Math.floor((e.clientY - rect.top) / ROW_H),
                                  0
                                ),
                                slotCount - 1
                              );
                              setHoverSlot({ key, idx });
                            }
                          : undefined
                      }
                      onMouseLeave={canPick ? () => setHoverSlot(null) : undefined}
                      title={`${patient}${locSuffix}${
                        locationIdentity && locLabel ? ` · ${locLabel}` : ""
                      } · ${fmt12(b.time.start_time)} – ${fmt12(b.time.end_time)}`}
                      className={`absolute rounded border text-left overflow-hidden flex flex-col items-stretch justify-start ${cls}${spot}`}
                      style={{
                        top,
                        height,
                        left: `calc(${b.lane * widthPct}% + 1px)`,
                        width: `calc(${widthPct}% - 2px)`,
                        ...(striped ? STRIPES : {}),
                        ...(locationIdentity
                          ? blockStyle(locEntry?.tint, statusKey)
                          : locEntry
                            ? { borderLeft: `3px solid ${locEntry.color.stripe}` }
                            : {}),
                        ...(statusKey === "cancelled" && locationIdentity
                          ? { textDecoration: "line-through" }
                          : {}),
                      }}
                    >
                      <span className="block px-1 text-[9px] font-semibold leading-[1.15] truncate relative z-[2]">
                        {locationIdentity && statusKey === "confirmed" && (
                          <Check
                            size={9}
                            data-testid={`cal-block-check-${key}`}
                            className="inline-block align-[-1px] mr-0.5"
                          />
                        )}
                        {fmt12(b.time.start_time)} {patient}
                        {locSuffix}
                        {b.request.type === "direct_booking" && (
                          <span data-testid={`cal-block-direct-${key}`}> · Direct</span>
                        )}
                        {locLabel && !printLocLine && (
                          <span data-testid={`cal-block-loc-${key}`} className="opacity-80"> · {locLabel}</span>
                        )}
                      </span>
                      {printLocLine && (
                        <span
                          data-testid={`cal-block-loc-${key}`}
                          className="block px-1 text-[9px] font-bold leading-[1.15] truncate relative z-[2] opacity-90"
                        >
                          {locLabel}
                        </span>
                      )}
                      {canPick && hovIdx != null && (
                        <span
                          data-testid={`slot-highlight-${key}`}
                          className="absolute inset-x-0 bg-emerald-400/60 border-y border-emerald-600 pointer-events-none flex items-center justify-center text-[8px] font-bold text-emerald-950"
                          style={{ top: hovIdx * ROW_H, height: ROW_H }}
                        >
                          {fmtMin12(b.startMin + hovIdx * 15)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
